import React, { useEffect, useState, useMemo } from "react";
import { apiFetch, apiDownload } from "./api";
import { formatData } from "../utils/expirare";

const FILTRE = [
  { id: "toate", text: "Toate" },
  { id: "chirie", text: "Chirii lunare" },
  { id: "refacturare", text: "Refacturări" },
  { id: "neachitate", text: "Neachitate" },
];

const LUNI = ["ianuarie","februarie","martie","aprilie","mai","iunie",
              "iulie","august","septembrie","octombrie","noiembrie","decembrie"];

function lunaFrumos(l) {
  if (!l || !/^\d{4}-\d{2}$/.test(l)) return l || "—";
  const [an, m] = l.split("-");
  return `${LUNI[parseInt(m, 10) - 1]} ${an}`;
}

function sumaNumar(s) {
  // acceptă atât '12.450,00' (RO) cât și '12450.00'
  let t = String(s || "").replace(/\s/g, "");
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(t);
  return isNaN(n) ? 0 : n;
}

export default function Facturi() {
  const [facturi, setFacturi] = useState([]);
  const [stare, setStare] = useState("loading");
  const [filtru, setFiltru] = useState("toate");
  const [descarc, setDescarc] = useState(null);

  useEffect(() => {
    apiFetch("/facturi")
      .then((data) => {
        setFacturi(data.facturi || []);
        setStare("ok");
      })
      .catch(() => setStare("error"));
  }, []);

  const afisate = useMemo(() => {
    return facturi.filter((f) => {
      if (filtru === "chirie") return f.tip === "chirie";
      if (filtru === "refacturare") return f.tip === "refacturare";
      if (filtru === "neachitate") return f.statusPlata !== "achitata";
      return true;
    });
  }, [facturi, filtru]);

  const totalNeachitat = useMemo(
    () =>
      facturi
        .filter((f) => f.statusPlata !== "achitata")
        .reduce((s, f) => {
          // v9.3: la facturile parțial achitate contează doar restul de plată
          if (f.statusPlata === "partial" && f.restDePlata !== null && f.restDePlata !== undefined) {
            return s + f.restDePlata;
          }
          return s + sumaNumar(f.suma);
        }, 0),
    [facturi]
  );

  async function descarcaPdf(f) {
    if (!f.arePdf) return;
    setDescarc(f.id);
    try {
      const blob = await apiDownload(`/facturi/${f.id}/pdf`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `factura-${f.tip}-${f.luna || f.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || "Nu am putut descărca factura.");
    } finally {
      setDescarc(null);
    }
  }

  // v9.3: descărcarea anexei Excel a facturii
  async function descarcaExcel(f) {
    if (!f.areExcel) return;
    setDescarc(`xl-${f.id}`);
    try {
      const blob = await apiDownload(`/facturi/${f.id}/excel`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `anexa-${f.tip}-${f.luna || f.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || "Nu am putut descărca anexa Excel.");
    } finally {
      setDescarc(null);
    }
  }

  return (
    <div className="facturi">
      <div className="dash__head">
        <h1>Facturi</h1>
        <p>Chiriile lunare și facturile de refacturare emise de LEASEIN.</p>
      </div>

      {stare === "loading" && <SkeletonFacturi />}

      {stare === "error" && (
        <div className="portal__loading">
          Nu am putut încărca facturile. Reîncearcă mai târziu.
        </div>
      )}

      {stare === "ok" && facturi.length === 0 && (
        <div className="portal__loading">
          Nu există facturi emise încă. Vor apărea aici imediat ce sunt disponibile.
        </div>
      )}

      {stare === "ok" && facturi.length > 0 && (
        <>
          {totalNeachitat > 0 && (
            <div className="facturi__sumar">
              <span className="dot dot--galben" />
              Ai facturi neachitate în valoare de{" "}
              <strong>{totalNeachitat.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} lei</strong>.
            </div>
          )}

          <div className="chips">
            {FILTRE.map((f) => (
              <button
                key={f.id}
                className={`chip ${filtru === f.id ? "chip--active" : ""}`}
                onClick={() => setFiltru(f.id)}
              >
                {f.text}
              </button>
            ))}
          </div>

          <p className="masini__count">
            {afisate.length} {afisate.length === 1 ? "factură" : "facturi"}
            {filtru !== "toate" ? ` (din ${facturi.length})` : ""}
          </p>

          {afisate.length === 0 ? (
            <div className="portal__loading">Nicio factură în această categorie.</div>
          ) : (
            <div className="facturi__tabel" key={filtru}>
              <div className="facturi__cap facturi__cap--cli">
                <span>Luna</span>
                <span>Tip</span>
                <span>Număr</span>
                <span>Data emiterii</span>
                <span>Scadență</span>
                <span>Sumă</span>
                <span>Status</span>
                <span></span>
              </div>
              {afisate.map((f) => (
                <div className="facturi__rand facturi__rand--cli" key={f.id}>
                  <span className="facturi__luna" data-label="Luna">{lunaFrumos(f.luna)}</span>
                  <span data-label="Tip">
                    <span className={`tipf tipf--${f.tip}`}>
                      {f.tip === "chirie" ? "Chirie" : "Refacturare"}
                    </span>
                  </span>
                  <span data-label="Număr">{f.numar || "—"}</span>
                  <span className="facturi__data" data-label="Data emiterii">{formatData(f.data)}</span>
                  <span className="facturi__data" data-label="Scadență">
                    {f.scadenta ? formatData(f.scadenta) : "—"}
                  </span>
                  <span className="facturi__suma" data-label="Sumă">
                    {f.suma ? `${f.suma} lei` : "—"}
                  </span>
                  <span data-label="Status">
                    {f.statusPlata === "achitata" ? (
                      <span className="pill pill--verde">Achitată</span>
                    ) : f.statusPlata === "partial" ? (
                      <span className="pill pill--galben" title={f.sumaAchitata ? `Achitat până acum: ${f.sumaAchitata} lei` : undefined}>
                        Parțial achitată
                        {f.restDePlata !== null && f.restDePlata !== undefined
                          ? ` — de plătit ${f.restDePlata.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} lei`
                          : ""}
                      </span>
                    ) : (
                      <span className="pill pill--galben">Neachitată</span>
                    )}
                  </span>
                  <span className="facturi__actiune">
                    {f.arePdf ? (
                      <button
                        className="btn-pdf"
                        onClick={() => descarcaPdf(f)}
                        disabled={descarc === f.id}
                      >
                        {descarc === f.id ? "Se descarcă…" : "PDF"}
                      </button>
                    ) : (
                      <span className="facturi__nopdf">—</span>
                    )}
                    {f.areExcel && (
                      <button
                        className="btn-pdf"
                        style={{ marginLeft: 6 }}
                        onClick={() => descarcaExcel(f)}
                        disabled={descarc === `xl-${f.id}`}
                        title="Descarcă anexa Excel a facturii"
                      >
                        {descarc === `xl-${f.id}` ? "Se descarcă…" : "Excel"}
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SkeletonFacturi() {
  return (
    <div className="facturi__tabel" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div className="facturi__rand facturi__rand--cli" key={i}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((j) => (
            <span key={j}><span className="skel skel--line" style={{ height: 14, width: "80%" }} /></span>
          ))}
        </div>
      ))}
    </div>
  );
}
